import { useEffect, useMemo, useRef, useState } from "react"
import CalendarMonth from "@mui/icons-material/CalendarMonth"
import ChevronLeft from "@mui/icons-material/ChevronLeft"
import ChevronRight from "@mui/icons-material/ChevronRight"
import { ALL_DAYS_ON_SITE_LABEL } from "../../utils/jobSiteDailyHours"
import { hasHoursOnDay } from "../../utils/jobSiteHoursDays"
import {
  addMonths,
  calendarMonthCells,
  formatLongDate,
  formatMonthYear,
  parseISODateLocal,
  todayISODate,
} from "../../utils/isoDate"

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

function asDateSet(dates?: Iterable<string>): Set<string> | undefined {
  if (dates == null) return undefined
  return dates instanceof Set ? dates : new Set(dates)
}

export default function JobSiteHoursCalendar({
  date,
  disabled = false,
  enabledDates,
  canGoPrevious = true,
  canGoNext = true,
  onPreviousDay,
  onNextDay,
  onDateChange,
  onMonthChange,
}: {
  date: string
  disabled?: boolean
  /** When set, only these ISO dates (hours > 0) are selectable. */
  enabledDates?: Iterable<string>
  canGoPrevious?: boolean
  canGoNext?: boolean
  onPreviousDay: () => void
  onNextDay: () => void
  onDateChange: (next: string) => void
  onMonthChange?: (year: number, monthIndex: number) => void
}) {
  const selected = parseISODateLocal(date)
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(selected.getFullYear())
  const [viewMonth, setViewMonth] = useState(selected.getMonth())
  const rootRef = useRef<HTMLDivElement>(null)
  const enabled = useMemo(() => asDateSet(enabledDates), [enabledDates])

  useEffect(() => {
    if (!open) return
    const current = parseISODateLocal(date)
    setViewYear(current.getFullYear())
    setViewMonth(current.getMonth())
  }, [open, date])

  useEffect(() => {
    if (!open) return
    onMonthChange?.(viewYear, viewMonth)
  }, [open, viewYear, viewMonth, onMonthChange])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onPointer)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onPointer)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const cells = useMemo(() => calendarMonthCells(viewYear, viewMonth), [viewYear, viewMonth])
  const today = todayISODate()

  const setView = (year: number, monthIndex: number) => {
    setViewYear(year)
    setViewMonth(monthIndex)
  }

  if (disabled) {
    return (
      <div
        className="inline-flex items-center self-start rounded-full bg-gray-100 px-4 py-2 text-sm text-gray-400"
        aria-disabled="true"
        title="Calendar applies to day totals"
      >
        {ALL_DAYS_ON_SITE_LABEL}
      </div>
    )
  }

  return (
    <div ref={rootRef} className="relative self-start">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPreviousDay}
          disabled={!canGoPrevious}
          className={
            canGoPrevious
              ? "p-2 rounded-full text-primary hover:bg-secondary-extra-light"
              : "p-2 rounded-full text-gray-300 cursor-not-allowed"
          }
          aria-label="Previous day"
        >
          <ChevronLeft />
        </button>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 font-medium whitespace-nowrap text-primary-dark shadow-sm hover:bg-secondary-extra-light"
          aria-label="Open calendar"
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <CalendarMonth fontSize="small" />
          <span>{formatLongDate(date)}</span>
        </button>
        <button
          type="button"
          onClick={onNextDay}
          disabled={!canGoNext}
          className={
            canGoNext
              ? "p-2 rounded-full text-primary hover:bg-secondary-extra-light"
              : "p-2 rounded-full text-gray-300 cursor-not-allowed"
          }
          aria-label="Next day"
        >
          <ChevronRight />
        </button>
      </div>

      {open && (
        <div
          role="dialog"
          aria-label="Choose a date"
          className="absolute left-0 z-20 mt-2 w-80 rounded-2xl bg-white p-4 shadow-xl border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              className="p-1 rounded-full text-primary hover:bg-secondary-extra-light"
              aria-label="Previous month"
              onClick={() => {
                const next = addMonths(viewYear, viewMonth, -1)
                setView(next.year, next.monthIndex)
              }}
            >
              <ChevronLeft />
            </button>
            <p className="font-semibold">{formatMonthYear(viewYear, viewMonth)}</p>
            <button
              type="button"
              className="p-1 rounded-full text-primary hover:bg-secondary-extra-light"
              aria-label="Next month"
              onClick={() => {
                const next = addMonths(viewYear, viewMonth, 1)
                setView(next.year, next.monthIndex)
              }}
            >
              <ChevronRight />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
            {WEEKDAYS.map((day) => (
              <span key={day} className="py-1">{day}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell) => {
              const selectedDay = cell.iso === date
              const isToday = cell.iso === today
              const selectable = enabled == null || hasHoursOnDay(enabled, cell.iso)
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={!selectable}
                  onClick={() => {
                    if (!selectable) return
                    onDateChange(cell.iso)
                    setOpen(false)
                  }}
                  className={[
                    "h-9 rounded-full text-sm",
                    !selectable
                      ? "text-gray-300 cursor-not-allowed"
                      : cell.inMonth ? "text-primary-dark" : "text-gray-400",
                    selectedDay && selectable ? "bg-primary text-white font-semibold" : "",
                    selectable && !selectedDay ? "hover:bg-secondary-extra-light" : "",
                    selectable && !selectedDay && isToday ? "ring-1 ring-primary" : "",
                  ].join(" ")}
                  aria-current={selectedDay ? "date" : undefined}
                  aria-disabled={!selectable}
                  aria-label={formatLongDate(cell.iso)}
                >
                  {cell.day}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
